import { useTranslation } from "react-i18next";
import React, { useEffect } from "react";
import classNames from "classnames";
import type { DraggableSyntheticListeners } from "@dnd-kit/core";
import type { Transform } from "@dnd-kit/utilities";

import { Handle } from "./components";

import styles from "./Item.module.scss";
import { detachedTitle } from "../../../../utils/convertQuillToString";
import { Dropdown, MenuProps, Space } from "antd";
import { CopyOutlined, SaveOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import FormulaRenderer from "../../../math-formula";
import { convertLatexToImage, sanitizeLatexInput } from "../../../../utils/app";

export interface Props {
  content: string;
  isActiveQuestion?: boolean;
  dragOverlay?: boolean;
  color?: string;
  disabled?: boolean;
  dragging?: boolean;
  handle?: boolean;
  handleProps?: any;
  height?: number;
  index?: number;
  fadeIn?: boolean;
  transform?: Transform | null;
  listeners?: DraggableSyntheticListeners;
  sorting?: boolean;
  style?: React.CSSProperties;
  transition?: string | null;
  wrapperStyle?: React.CSSProperties;
  value: React.ReactNode;
  onRemove?(): void;
  onClick?: (id: any) => void;
  onDropDownClick?: (key: string, id: any) => void;
  renderItem?(args: {
    isActiveQuestion: boolean;
    dragOverlay: boolean;
    dragging: boolean;
    sorting: boolean;
    index: number | undefined;
    fadeIn: boolean;
    listeners: DraggableSyntheticListeners;
    ref: React.Ref<HTMLElement>;
    style: React.CSSProperties | undefined;
    transform: Props["transform"];
    transition: Props["transition"];
    value: Props["value"];
  }): React.ReactElement;
}

export const Item = React.memo(
  React.forwardRef<HTMLLIElement, Props>(
    (
      {
        content,
        isActiveQuestion,
        dragOverlay,
        dragging,
        disabled,
        fadeIn,
        handle,
        handleProps,
        height,
        index,
        listeners,
        onRemove,
        onClick = (id: any) => ({}),
        onDropDownClick = (key: string, id: any) => ({}),
        renderItem,
        sorting,
        style,
        transition,
        transform,
        value,
        wrapperStyle,
        ...props
      },
      ref
    ) => {
      useEffect(() => {
        if (!dragOverlay) {
          return;
        }

        document.body.style.cursor = "grabbing";

        return () => {
          document.body.style.cursor = "";
        };
      }, [dragOverlay]);
      const menuItems: MenuProps["items"] = [
        {
          label: "Tạo bản sao",
          key: "1",
          icon: <CopyOutlined />,
        },
        // {
        //   label: "Lưu vào ngân hàng câu hỏi",
        //   key: "2",
        //   icon: <SaveOutlined />,
        // },
        {
          label: "Xóa",
          key: "3",
          icon: <DeleteOutlined />,
          danger: true,
        },
      ];
      return renderItem ? (
        renderItem({
          dragOverlay: Boolean(dragOverlay),
          isActiveQuestion: Boolean(isActiveQuestion),
          dragging: Boolean(dragging),
          sorting: Boolean(sorting),
          index,
          fadeIn: Boolean(fadeIn),
          listeners,
          ref,
          style,
          transform,
          transition,
          value
        })
      ) : (
        <li
          className={classNames(
            styles.Wrapper,
            fadeIn && styles.fadeIn,
            sorting && styles.sorting,
            dragOverlay && styles.dragOverlay,
            isActiveQuestion && 'section-record-active',
          )}
          style={
            {
              ...wrapperStyle,
              transition: [transition, wrapperStyle?.transition]
                .filter(Boolean)
                .join(", "),
              "--translate-x": transform
                ? `${Math.round(transform.x)}px`
                : undefined,
              "--translate-y": transform
                ? `${Math.round(transform.y)}px`
                : undefined,
              "--scale-x": transform?.scaleX
                ? `${transform.scaleX}`
                : undefined,
              "--scale-y": transform?.scaleY
                ? `${transform.scaleY}`
                : undefined,
              "--index": index,
            } as React.CSSProperties
          }
          ref={ref}
        >
          <div
            className={classNames(
              styles.Item,
              dragging && styles.dragging,
              handle && styles.withHandle,
              dragOverlay && styles.dragOverlay,
              disabled && styles.disabled,
              "question-col"
            )}
            style={style}
            data-cypress="draggable-item"
            {...(!handle ? listeners : undefined)}
            {...props}
            tabIndex={!handle ? 0 : undefined}
          >
            <span className={styles.Actions}>
              {handle ? <Handle {...handleProps} {...listeners} /> : null}
            </span>
            <div className={`${styles.Content} question-content`} onClick={() => { onClick(Number(value)) }} style={{display: "flex"}}>
              <div className="mr-1">{index !== undefined && index + 1}.</div>
              <div dangerouslySetInnerHTML={{__html: `${convertLatexToImage(content)}`}}></div>
            </div>
            <Dropdown
              trigger={["click"]}
              className={styles.Dropdown}
              menu={{
                items: menuItems,
                onClick: ({ key }) => {
                  onDropDownClick(key, Number(value));
                },
              }}>
              <a onClick={(e: any) => e.preventDefault()}>
                <Space>
                  <MoreOutlined />
                </Space>
              </a>
            </Dropdown>
          </div>
        </li>
      );
    }
  )
);
